import { AssetCache } from './cache.js';
import { Component } from './live-codec.js';

const STOP = 4, STOP_LOOPING = 32, SPEAKER = 64, KEEP_CHANNEL = 1024, LOOP = 2048, GLOBAL = 4096;
const MAX_FILE = 32 * 1024 * 1024;
const MAX_CACHED_EFFECT = 1024 * 1024;
function withSoundDeadline(promise, milliseconds = 15000) {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Sound load timed out')), milliseconds);
    })]).finally(() => clearTimeout(timer));
}
export const SOUND_VOLUME_KEY = 'saxtonhell.live.soundVolume';
export const VOICE_VOLUME_KEY = 'saxtonhell.live.voiceVolume';

/** Read Source cue-loop or RIFF smpl metadata; audio decoding stays native. */
export function waveInfo(bytes) {
    bytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const tag = at => String.fromCharCode(...bytes.subarray(at, at + 4));
    if (bytes.length < 12 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return {};
    let rate = 0, format, cue, loop;
    const end = Math.min(bytes.length, view.getUint32(4, true) + 8);
    for (let at = 12, chunks = 0; at + 8 <= end && chunks < 256; chunks++) {
        const size = view.getUint32(at + 4, true), start = at + 8;
        if (size > end - start) throw new Error('Truncated WAV chunk');
        if (tag(at) === 'fmt ' && size >= 16) { format = view.getUint16(start, true); rate = view.getUint32(start + 4, true); }
        if (tag(at) === 'cue ' && size >= 28 && view.getUint32(start, true)) cue = view.getUint32(start + 24, true);
        if (tag(at) === 'smpl' && size >= 60 && view.getUint32(start + 28, true))
            loop = [view.getUint32(start + 44, true), view.getUint32(start + 48, true) + 1];
        at = start + size + (size & 1);
    }
    return { format, loopStart: rate ? (loop?.[0] ?? cue ?? 0) / rate : 0, loopEnd: rate && loop ? loop[1] / rate : undefined };
}

// Use one mix gain for effects, announcements and music; global/level-zero
// flags control spatialization only. The user's sound volume applies afterward.
function playbackVolume(sound) { return sound.volume * 0.25; }

/** Sound preference is independent of the browser's autoplay gate. */
export class LiveAudioOutput {
    constructor({ contextFactory = () => new AudioContext(), diagnostic = () => {}, storage, now = () => performance.now() } = {}) {
        this.now = now; this.stateListeners = new Set();
        this.contextFactory = contextFactory; this.diagnostic = diagnostic; this.enabled = true; this.allowed = false; this.volume = 0.25; this.voiceVolume = 0.25; this.voiceEnabled = false;
        try {
            this.storage = storage ?? globalThis.localStorage;
            const voice = this.storage?.getItem(VOICE_VOLUME_KEY);
            if (voice != null && voice.trim() && Number.isFinite(Number(voice)) && Number(voice) >= 0 && Number(voice) <= 1) this.voiceVolume = Number(voice);
            const stored = this.storage?.getItem(SOUND_VOLUME_KEY);
            if (stored !== null && stored !== undefined && stored.trim() !== '' && Number.isFinite(Number(stored)) && Number(stored) >= 0 && Number(stored) <= 1) this.volume = Number(stored);
        } catch { /* Private browsing and disabled storage still allow sound. */ }
    }
    async enable() {
        this.enabled = true; this.syncVolume();
        await this.unlock();
    }
    async unlock() {
        if (!this.enabled && !this.voiceEnabled) return;
        if (!this.context) {
            this.context = this.contextFactory();
            this.context.onstatechange = () => {
                for (const listener of this.stateListeners) listener(this.context.state);
                this.recover();
            };
        }
        if (!this.master) { this.master = this.context.createGain(); this.master.connect(this.context.destination); }
        if (!this.voiceMaster) { this.voiceMaster = this.context.createGain(); this.voiceMaster.connect(this.context.destination); }
        this.syncVolume();
        await this.context.resume();
        this.unlocked ||= this.context.state === 'running'; this.syncVolume();
    }
    onStateChange(listener) { this.stateListeners.add(listener); return () => this.stateListeners.delete(listener); }
    recover() {
        const context = this.context;
        // System/device interruptions can suspend an already unlocked context
        // while the visible scene continues. Retry at most once per second;
        // initial autoplay permission and explicit mute still require user input.
        if (this.closed || !this.unlocked || (!this.enabled && !this.voiceEnabled) || !this.allowed || !context
            || context.state === 'running' || context.state === 'closed' || this.recovering
            || this.now() < (this.retryAt ?? 0)) return;
        this.retryAt = this.now() + 1000; this.recovering = true;
        if (!this.recoveryStarted) { this.recoveryStarted = true; this.diagnostic('Browser audio paused; restoring playback.'); }
        let resumed;
        try { resumed = context.resume(); }
        catch (error) { resumed = Promise.reject(error); }
        void Promise.resolve(resumed).then(() => {
            this.syncVolume();
            if (context.state === 'running') { this.recoveryReported = false; this.recoveryStarted = false; }
        }).catch(error => {
            if (!this.recoveryReported && !this.closed) {
                this.recoveryReported = true; this.diagnostic(`Audio recovery delayed: ${error.message}`);
            }
        }).finally(() => { this.recovering = false; });
    }
    mute() { this.enabled = false; this.syncVolume(); }
    setVolume(volume) {
        if (!Number.isFinite(volume)) return;
        this.volume = Math.max(0, Math.min(1, volume)); this.syncVolume();
        try { this.storage?.setItem(SOUND_VOLUME_KEY, String(this.volume)); } catch {}
    }
    setVoiceEnabled(enabled) { this.voiceEnabled = enabled; this.syncVolume(); }
    setVoiceVolume(volume) {
        if (!Number.isFinite(volume)) return;
        this.voiceVolume = Math.max(0, Math.min(1, volume)); this.syncVolume();
        try { this.storage?.setItem(VOICE_VOLUME_KEY, String(this.voiceVolume)); } catch {}
    }
    setLive(allowed) { this.allowed = allowed; this.syncVolume(); this.recover(); }
    syncVolume() {
        if (this.master) this.master.gain.value = this.enabled && this.allowed ? this.volume : 0;
        if (this.voiceMaster) this.voiceMaster.gain.value = this.allowed && this.voiceEnabled ? this.voiceVolume : 0;
    }
    dispose() { this.closed = true; this.mute(); this.stateListeners.clear(); if (this.context) this.context.onstatechange = null; void this.context?.close(); }
}

/** Sounds are absolute channel state, so snapshots, coalescing and late loads
 * seek into active clips rather than replaying their beginnings. */
export class LiveSounds {
    constructor(output, rpc, { diagnostic = () => {}, concurrency = 4, maxVoices = 128,
        mediaFactory = () => new Audio(), fetchAudio, fetchMetadata = async url => {
            const response = await fetch(url, { cache: 'no-cache', signal: AbortSignal.timeout(15000) });
            if (!response.ok) throw new Error(`Sound metadata unavailable (${response.status})`);
            return response.json();
        } } = {}) {
        Object.assign(this, { output, rpc, diagnostic, concurrency, maxVoices, mediaFactory, fetchMetadata });
        this.abort = new AbortController();
        this.fetchAudio = fetchAudio ?? ((url, signal) => {
            // Shared across sound/announcer channels and maps; IndexedDB also
            // retains validated, versioned downloads between visits.
            output.assetCache ??= new AssetCache(diagnostic);
            return output.assetCache.fetch(url, signal);
        });
        this.entries = new Map(); this.resources = new Map(); this.running = 0; this.closed = false; this.time = 0; this.reported = new Set();
    }
    reconcile(entities, dictionary) {
        const present = new Set();
        const arrivalTime = this.time || null;
        for (const [key, entity] of entities) {
            const sound = entity.state.components[Component.Sound]; if (!sound) continue;
            present.add(key);
            const path = dictionary.get(`4:${sound.sound}`);
            let entry = this.entries.get(key);
            if (entry && (entry.sound.startTime !== sound.startTime || entry.path !== path)) { this.stop(entry); this.entries.delete(key); entry = null; }
            if (!entry) { entry = { key, path, sound, entity, attempted: false, version: 0, arrivalTime }; this.entries.set(key, entry); }
            entry.sound = sound; entry.entity = entity;
            if (sound.flags & STOP) { this.stop(entry); entry.attempted = true; }
            if (entry.media) { entry.gain.gain.value = playbackVolume(sound); entry.media.playbackRate = sound.pitch / 100; entry.media.loop = !!(sound.flags & LOOP) && !(sound.flags & STOP_LOOPING); }
        }
        for (const [key, entry] of this.entries) if (!entry.local && !present.has(key)) { this.stop(entry); this.entries.delete(key); }
    }
    local(identity, path, sound, validUntil) {
        if (this.closed || !this.output?.enabled || !this.output.allowed || this.output.context?.state !== 'running' || this.time > validUntil) return;
        const key = `announcer:${identity}`;
        if (this.entries.has(key)) return;
        this.entries.set(key, { key, path, sound, local: true, validUntil, attempted: false, version: 0, onset: null });
    }
    stop(entry) {
        entry.version++;
        if (entry.media) {
            entry.media.onloadedmetadata = entry.media.onended = entry.media.onerror = entry.media.ontimeupdate = null;
            entry.media.pause(); entry.media.removeAttribute('src'); entry.media.load();
        }
        entry.source?.disconnect(); entry.gain?.disconnect(); entry.panner?.disconnect();
        for (const node of entry.effects ?? []) node.disconnect();
        entry.effects = [];
        if (entry.resource) {
            entry.resource.voices--;
            this.release(entry.resource);
        }
        entry.source = entry.media = entry.gain = entry.panner = entry.resource = null;
    }
    silence() { for (const entry of this.entries.values()) { if (entry.media || entry.loading) this.stop(entry); entry.attempted = false; entry.onset = undefined; entry.arrivalTime = null; } }
    resource(path) {
        let record = this.resources.get(path);
        if (record) { this.resources.delete(path); this.resources.set(path, record); return record.promise; }
        record = { promise: null }; this.resources.set(path, record);
        record.promise = (async () => {
            const resource = await withSoundDeadline(this.rpc('sound', { path }));
            if (this.closed) return null;
            if (resource.bytes) {
                if (resource.bytes.byteLength > MAX_FILE) throw new Error('Sound exceeds the 32 MiB file limit');
                record.size = resource.bytes.byteLength;
                record.value = { ...resource, info: waveInfo(resource.bytes), voices: 0 };
                this.trim(); return record.value;
            }
            // Metadata skips PCM server-side. Small effects share one complete
            // download; large/unknown clips keep progressive native playback.
            const info = await withSoundDeadline(this.fetchMetadata(resource.metadataUrl));
            // Asset sets include provider generations, but loose-file replacements
            // need their own revision. Native media can reuse/cache byte ranges
            // only when that exact version has a fresh, strong HTTP validator.
            const url = info.revision ? `${resource.url}${resource.url.includes('?') ? '&' : '?'}revision=${encodeURIComponent(info.revision)}` : resource.url;
            let bytes;
            if (Number.isSafeInteger(info.length) && info.length > 0 && info.length <= MAX_CACHED_EFFECT) {
                bytes = await withSoundDeadline(this.fetchAudio(url, AbortSignal.any([this.abort.signal, AbortSignal.timeout(15000)])));
                if (this.closed) return null;
                if (bytes.byteLength > MAX_CACHED_EFFECT) throw new Error('Sound effect exceeds cache limit');
                record.size = bytes.byteLength;
            }
            record.value = { ...resource, url, info, bytes, voices: 0 };
            this.trim(); return record.value;
        })().catch(error => {
            // A failed request must not poison every later emission of this file.
            if (this.resources.get(path) === record) this.resources.delete(path);
            throw error;
        });
        this.trim();
        return record.promise;
    }
    trim() {
        let bytes = 0;
        for (const record of this.resources.values()) bytes += record.size ?? 0;
        for (const [path, record] of this.resources) {
            if (this.resources.size <= 256 && bytes <= 64 * 1024 * 1024) break;
            if (!record.value) continue; // Never duplicate an in-flight download.
            bytes -= record.size ?? 0; this.resources.delete(path);
            record.value.evicted = true; this.release(record.value);
        }
    }
    release(resource) {
        if (resource.evicted && !resource.voices && resource.blobUrl) {
            URL.revokeObjectURL(resource.blobUrl); resource.blobUrl = null;
        }
    }
    report(entry, error, retry = false) {
        if (this.closed || this.entries.get(entry.key) !== entry) return;
        if (!this.reported.has(entry.path)) {
            this.reported.add(entry.path);
            if (this.reported.size > 256) this.reported.delete(this.reported.values().next().value);
            this.diagnostic(`Sound ${entry.path}: ${error.message}`);
        }
        this.stop(entry);
        entry.failures = (entry.failures ?? 0) + 1;
        entry.attempted = !retry || entry.failures >= 3;
        entry.retryAt = this.time + 500 * entry.failures;
    }
    frame(time, camera, entities) {
        this.time = time;
        for (const [key, entry] of this.entries) if (entry.local && (time > entry.sound.startTime + 180000 || !this.output?.enabled || !this.output.allowed)) { this.stop(entry); this.entries.delete(key); }
        const { output } = this; output?.recover?.();
        const context = output?.context;
        if (!output?.enabled || !output.allowed || !context || context.state !== 'running') { this.silence(); return; }
        if (camera) {
            const position = camera.position.toArray();
            const direction = camera.getWorldDirection(camera.position.clone()).toArray();
            const up = camera.position.clone().set(0, 1, 0).applyQuaternion(camera.quaternion).toArray();
            this.listener(context.listener, position, direction, up);
        }
        // Auto and static channels allow several clips. Other channels belong
        // to one exact owner generation; a newer clip replaces that voice.
        const channels = new Map();
        for (const entry of [...this.entries.values()].sort((a, b) => a.sound.startTime - b.sound.startTime)) {
            const sound = entry.sound;
            if ((entry.attempted && !entry.media && !entry.loading) || sound.flags & STOP || sound.startTime > time || !sound.channel || sound.channel === 6) continue;
            const key = `${sound.ownerIndex}:${sound.ownerGeneration}:${sound.channel}`, old = channels.get(key);
            if (old && old !== entry) {
                const loser = sound.flags & KEEP_CHANNEL ? entry : old;
                this.stop(loser); loser.attempted = true;
                if (loser === entry) continue;
            }
            channels.set(key, entry);
        }
        let voices = 0;
        for (const entry of this.entries.values()) if (entry.media || entry.loading) voices++;
        for (const entry of this.entries.values()) {
            const sound = entry.sound;
            if (entry.media) {
                // Native media can stall without an error/ended event. Release
                // its voice slot and retry instead of starving every new sound.
                if (entry.media.currentTime !== entry.progressTime) { entry.progressTime = entry.media.currentTime; entry.progressAt = time; }
                if (time - entry.progressAt > 15000) this.report(entry, new Error('Audio playback stalled'), true);
                else this.wrapLoop(entry);
            }
            if (entry.panner) {
                const owner = entities?.get(`${sound.ownerIndex}:${sound.ownerGeneration}`);
                const pos = owner?.object.getWorldPosition
                    ? owner.object.getWorldPosition(owner.object.position.clone()).toArray()
                    : owner?.object.position.toArray() ?? entry.entity.state.components[Component.Transform].position;
                this.position(entry.panner, pos, context.currentTime);
            }
            if (entry.attempted || entry.loading || (entry.retryAt ?? 0) > time || sound.flags & STOP || !entry.path || sound.startTime > time) continue;
            if (this.running >= this.concurrency || voices >= this.maxVoices) break;
            const version = entry.version;
            // Freeze the onset of a just-arrived one-shot while its first asset
            // request loads. Otherwise a short shot can finish before metadata arrives.
            if (!entry.local) entry.onset ??= !(sound.flags & LOOP) && (entry.arrivalTime ?? time) - sound.startTime <= 250 && time - sound.startTime <= 5000 ? Math.max(entry.arrivalTime ?? time, sound.startTime) : null;
            entry.loading = true; this.running++; voices++;
            void this.resource(entry.path).then(resource => {
                if (this.closed || entry.version !== version || this.entries.get(entry.key) !== entry || entry.sound.flags & STOP || !output.enabled || !output.allowed || !resource) return;
                if (entry.local && this.time > entry.validUntil) { entry.attempted = true; return; }
                entry.attempted = true; this.play(entry, resource);
            }).catch(error => { if (entry.version === version) this.report(entry, error, true); })
                .finally(() => { entry.loading = false; this.running--; });
        }
    }
    play(entry, resource) {
        const context = this.output.context, sound = entry.sound;
        const media = entry.media = this.mediaFactory(), source = entry.source = context.createMediaElementSource(media);
        const gain = entry.gain = context.createGain(); gain.gain.value = playbackVolume(sound);
        media.preload = 'auto'; media.playbackRate = sound.pitch / 100; media.preservesPitch = false;
        media.loop = !!(sound.flags & LOOP) && !(sound.flags & STOP_LOOPING); entry.info = resource.info;
        entry.lastMediaTime = 0; entry.progressTime = 0; entry.progressAt = this.time;
        source.connect(gain);
        let tail = gain;
        entry.effects = [];
        if (sound.flags & SPEAKER && context.createBiquadFilter) {
            const filter = context.createBiquadFilter(); filter.type = 'bandpass'; filter.frequency.value = 1700; filter.Q.value = 0.7;
            tail.connect(filter); tail = filter; entry.effects.push(filter);
        }
        // Source's proprietary DSP presets have no browser equivalent. A
        // bounded, decaying echo gives spatial presets a room-like response,
        // while preserving native progressive WAV/MP3 playback.
        if (sound.dsp && context.createDelay) {
            const delay = context.createDelay(1), feedback = context.createGain(), wet = context.createGain(), mix = context.createGain();
            delay.delayTime.value = 0.06 + Math.min(sound.dsp, 30) * 0.004;
            feedback.gain.value = 0.25; wet.gain.value = 0.2;
            tail.connect(mix); tail.connect(delay); delay.connect(feedback); feedback.connect(delay); delay.connect(wet); wet.connect(mix);
            entry.effects.push(delay, feedback, wet, mix); tail = mix;
        }
        if (!(sound.flags & GLOBAL) && sound.level > 0) {
            const panner = entry.panner = context.createPanner(); panner.panningModel = 'HRTF'; panner.distanceModel = 'inverse';
            const attenuation = sound.level > 50 ? 20 / (sound.level - 50) : 4;
            panner.refDistance = 216 / attenuation; panner.maxDistance = 100000; panner.rolloffFactor = 0.3;
            this.position(panner, entry.entity.state.components[Component.Transform].position, context.currentTime);
            tail.connect(panner); panner.connect(this.output.master);
        } else tail.connect(this.output.master);
        const active = () => entry.media === media && !this.closed && this.output.enabled && this.output.allowed;
        media.onloadedmetadata = () => {
            if (!active()) return;
            if (entry.local && this.time > entry.validUntil) { this.stop(entry); return; }
            let offset = Math.max(0, ((entry.onset ?? this.time) - entry.sound.startTime) / 1000 * media.playbackRate);
            const start = Math.min(resource.info.loopStart ?? 0, media.duration);
            const end = Math.min(resource.info.loopEnd ?? media.duration, media.duration);
            if (media.loop && end > start && offset >= end) offset = start + (offset - start) % (end - start);
            else if (offset >= media.duration) {
                if (entry.onset !== null && entry.onset !== undefined) offset = 0;
                else { this.stop(entry); return; }
            }
            media.currentTime = offset; entry.lastMediaTime = offset;
            // play() waits only until enough media is buffered; never wait for
            // canplaythrough, fetch.arrayBuffer(), or decodeAudioData().
            void media.play().catch(error => { if (active()) this.report(entry, error, true); });
        };
        media.ontimeupdate = () => { if (active()) this.wrapLoop(entry); };
        media.onended = () => { if (entry.media === media) this.stop(entry); };
        media.onerror = () => { if (active()) this.report(entry, new Error('Native audio playback failed; this file may require transcoding to PCM WAV or MP3')); };
        if (resource.bytes && !resource.blobUrl) {
            resource.blobUrl = URL.createObjectURL(new Blob([resource.bytes], { type: entry.path.endsWith('.mp3') ? 'audio/mpeg' : 'audio/wav' }));
            delete resource.bytes; // The cached Blob owns the bytes now.
        }
        entry.resource = resource; resource.voices++;
        media.src = resource.blobUrl ?? resource.url;
        media.load();
    }
    wrapLoop(entry) {
        const media = entry.media;
        if (!media?.loop || !Number.isFinite(media.duration)) return;
        const start = Math.min(entry.info.loopStart ?? 0, media.duration);
        const end = Math.min(entry.info.loopEnd ?? media.duration, media.duration);
        if (end > start && (media.currentTime >= end || media.currentTime < entry.lastMediaTime && media.currentTime < start))
            media.currentTime = start + Math.max(0, media.currentTime - end) % (end - start);
        entry.lastMediaTime = media.currentTime;
    }
    position(node, values, time) {
        if (node.positionX) ['X', 'Y', 'Z'].forEach((axis, i) => node[`position${axis}`].setValueAtTime(values[i], time));
        else node.setPosition(...values);
    }
    listener(listener, position, forward, up) {
        if (listener.positionX) {
            const time = this.output.context.currentTime;
            for (const [prefix, values] of [['position', position], ['forward', forward], ['up', up]])
                ['X', 'Y', 'Z'].forEach((axis, i) => listener[`${prefix}${axis}`].setValueAtTime(values[i], time));
        } else { listener.setPosition(...position); listener.setOrientation(...forward, ...up); }
    }
    clear() { this.silence(); this.entries.clear(); }
    dispose() {
        this.closed = true; this.abort.abort(); this.silence(); this.entries.clear();
        for (const record of this.resources.values()) if (record.value) { record.value.evicted = true; this.release(record.value); }
        this.resources.clear();
    }
}
